// @flow

import { createSelector } from 'reselect';
import {List, Map as ImmutableMap, OrderedMap, Set as ImmutableSet} from 'immutable';
import { getInstitutionLoginsById } from 'data/institutionLogins/selectors';
import createCachedSelector, { LruObjectCache, LruMapCache } from 're-reselect';

import { getFeatureFlags } from 'utils/featureFlags/selectors';

import { REDUCER_KEY } from './reducer';
import type { AccountsStore, AccountRecordTypes } from './types';

/*
 * For these selectors, state is global state as it is provided to React (not specific to accounts)
 * We use them to select into the store and get data for this REDUCER_KEY
 */
export const getAccountStore = (state : any): AccountsStore => state[REDUCER_KEY];
export const getLoadPending = (state: AccountsStore) => getAccountStore(state).loadPending;
export const getIsLoading = (state: AccountsStore) => getAccountStore(state).isLoading;
export const getLastSyncDate = (state: AccountsStore) => getAccountStore(state).lastSyncDate;

// updated to not include Goal accounts
export const getAccountsById = createSelector(
  (state: AccountsStore) => getAccountStore(state).resourcesById,
  (accountsById) => accountsById.filter((acct) => acct.type !== 'GOAL'),
);
export const getAccountsByIdIncludingGoals = (state: AccountsStore) => getAccountStore(state).resourcesById;

export const isAssetLiabilityAccount = (account: AccountRecordTypes) =>
  account.type === 'LOAN' || account.type === 'OTHER_ASSET' || account.type === 'OTHER_LIABILITY' ||
  account.type === 'REAL_ESTATE' || account.type === 'VEHICLE';

export const isCashAccount = (account: AccountRecordTypes) =>
  account.type === 'CREDIT' || account.type === 'BANK' || account.type === 'CASH';

export const isGoalAccount = (account: AccountRecordTypes) =>
  account?.type === 'GOAL';

export const isInvestmentAccount = (account: AccountRecordTypes) =>
  account.type === 'INVESTMENT';

export const isLoanAccount = (account: AccountRecordTypes) =>
  account.type === 'LOAN';

export const getReportableAccountsById = createSelector(
  getAccountsById,
  (accountsById) => accountsById.filter((x) => !x.isExcludedFromReports)
);

export const getHasReportableAccounts = createSelector(
  getReportableAccountsById,
  (accountsById) => accountsById.size > 0,
);

export const getVisibleAccountsById = createSelector(
  getAccountsById,
  (accountsById) => accountsById.filter((x) => !x.isExcludedFromAccountBar)
);

export const getInvestmentAccounts = createSelector(
  getAccountsById,
  (accountsById) => (
    accountsById == null ? new List() : accountsById.filter((x) => isInvestmentAccount(x))
  )
);

export const getInvestmentManualAccounts = createSelector(
  getInvestmentAccounts,
  (accountsById) => accountsById && accountsById.filter((account) => !account.aggregators),
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a the set of all account IDs. The returned set will only change when an
// account is added/deleted.
//-------------------------------------------------------------------------------------------------
export const getAllAccountIds = createSelector(
  getAccountsByIdIncludingGoals,
  function() { // eslint-disable-line func-names
    let allAccountIds = ImmutableSet();
    return (accountsById) => {
      const addedAccountsById = accountsById.filter((_, id) => !allAccountIds.has(id));
      const removedAccountIds = allAccountIds.filter((id) => !accountsById.has(id));
      if (!addedAccountsById.size && !removedAccountIds.size) {
        return allAccountIds;
      }
      allAccountIds = allAccountIds.withMutations((set) => {
        addedAccountsById.forEach((_, id) => set.add(id));
        removedAccountIds.forEach((id) => set.add(id));
      });
      return allAccountIds;
    };
  }()
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a map of account types keyed by the account ID. The returned value will
// only change when accounts are added/deleted or when an account type has changed.
//-------------------------------------------------------------------------------------------------
export const getAccountTypesById = createSelector(
  getAccountsByIdIncludingGoals,
  function() { // eslint-disable-line func-names
    let accountTypesById = ImmutableMap();
    return (accountsById) => {
      const removedAccountTypesById = accountTypesById.filter((_, id) => !accountsById.has(id));
      accountTypesById = accountTypesById.withMutations((map) => {
        removedAccountTypesById.forEach((_, id) => map.remove(id));
        accountsById.forEach((account, accountId) => map.set(accountId, account.type));
      });
      return accountTypesById;
    };
  }()
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a the set of goal account IDs. This can be used to quickly determine whether
// an account is a GOAL account or not. The returned set will only change when a goal account is
// added/deleted and an existing account has changed to/from a GOAL account.
//-------------------------------------------------------------------------------------------------
export const getGoalAccountIds = createSelector(
  getAccountTypesById,
  function combinerClosure() { // if ur are lame use this ==> eslint-disable-line func-names  :-)
    let goalAccountIds = ImmutableSet();
    return (accountTypesById) => {
      const removedGoalAccountIds = goalAccountIds.filter((id) => !accountTypesById.has(id));
      goalAccountIds = goalAccountIds.withMutations((set) => {
        removedGoalAccountIds.forEach((id) => set.remove(id));
        accountTypesById.forEach((type, id) => {
          if (type === 'GOAL') {
            set.add(id);
          } else {
            set.remove(id);
          }
        });
      });
      return goalAccountIds;
    };
  }()
);

export const getAccountsByInstitutionLoginId = createSelector(
  getAccountsByIdIncludingGoals,
  (accountsById) =>
    accountsById.reduce((accountsByInstitutionLoginIds, account) => {
      const institutionLoginId = account.institutionLoginId || '0';
      const accounts = accountsByInstitutionLoginIds.get(institutionLoginId) || List();
      return accountsByInstitutionLoginIds.set(institutionLoginId, accounts.push(account));
    }, ImmutableMap())
);

export const getAccountsForInstitutionLoginId = createSelector(
  getAccountsByInstitutionLoginId,
  (state, props) => props.institutionLoginId,
  (accountsByInstitutionId, institutionLoginId) =>
    accountsByInstitutionId.get(institutionLoginId) || List()
);

// This selector returns all "pure" manual accounts. A pure manual is an account that either does not belong to an
// institution-login or belongs to an "unconnected" institution-login. While QCS supports accounts being grouped into
// unconnected-logins, it is only used by Windows to group all manual accounts into a single unconnectted
// intitution-login (this isn't required but is a holdover from Windows syncing to Mint).
//
export const getPureManualAccounts = createSelector(
  getInstitutionLoginsById,
  getAccountsByInstitutionLoginId,
  (institutionLoginsById, accountsByInstitutionId) => {
    const pureManualAccounts = accountsByInstitutionId.reduce((list, accounts, institutionLoginId) => {
      const institutionLogin = institutionLoginsById.get(institutionLoginId);
      if (Number(institutionLoginId) === 0 || (institutionLogin && Number(institutionLogin.institutionId) === 0)) {
        accounts.forEach((account) => list.push(account));
      }
      return list;
    }, []);

    return List(pureManualAccounts);
  }
);

const spendingAccountsOrder = {
  'CREDIT:CREDIT_CARD': 0,
  'BANK:CHECKING': 1,
  'CASH:': 2,
  'CREDIT:LINE_OF_CREDIT': 3,
  'CREDIT:OTHER_CREDIT': 4,
  'BANK:OTHER_BANK': 5,
};
export const getSpendingAccounts = createSelector(
  getVisibleAccountsById,
  (accounts: any) => OrderedMap(accounts.filter((account) => Object.prototype.hasOwnProperty.call(spendingAccountsOrder, `${account.type}:${account.subType}`)))
    .sort((account1, account2) => spendingAccountsOrder[`${account1.type}:${account1.subType}`] - spendingAccountsOrder[`${account2.type}:${account2.subType}`]),
);

const incomeAccountsOrder = {
  'BANK:CHECKING': 0,
  'BANK:OTHER_BANK': 1,
  'CASH:': 2,
  'BANK:SAVINGS': 3,
  'BANK:CD': 4,
  'BANK:MONEY_MARKET': 5,
};
export const getIncomeAccounts = createSelector(
  getVisibleAccountsById,
  (accounts: any) => OrderedMap(accounts.filter((account) => Object.prototype.hasOwnProperty.call(incomeAccountsOrder, `${account.type}:${account.subType}`)))
    .sort((account1, account2) => incomeAccountsOrder[`${account1.type}:${account1.subType}`] - incomeAccountsOrder[`${account2.type}:${account2.subType}`]),
);

const billAccountsOrder = {
  'BANK:CHECKING': 0,
  'CREDIT:CREDIT_CARD': 1,
  'CASH:': 2,
  'BANK:OTHER_BANK': 3,
  'CREDIT:OTHER_CREDIT': 4,
  'CREDIT:LINE_OF_CREDIT': 5,
  'BANK:MONEY_MARKET': 6,
};
export const getBillAccounts = createSelector(
  getVisibleAccountsById,
  (accounts: any) => OrderedMap(accounts.filter((account) => Object.prototype.hasOwnProperty.call(billAccountsOrder, `${account.type}:${account.subType}`)))
    .sort((account1, account2) => billAccountsOrder[`${account1.type}:${account1.subType}`] - billAccountsOrder[`${account2.type}:${account2.subType}`]),
);

const savingAccountsOrder = {
  'BANK:SAVINGS': 0,
  'BANK:CD': 1,
  'BANK:MONEY_MARKET': 2,
};
export const getSavingAccounts = createSelector(
  getVisibleAccountsById,
  (accounts: any) => OrderedMap(accounts.filter((account) => Object.prototype.hasOwnProperty.call(savingAccountsOrder, `${account.type}:${account.subType}`)))
    .sort((account1, account2) => savingAccountsOrder[`${account1.type}:${account1.subType}`] - savingAccountsOrder[`${account2.type}:${account2.subType}`]),
);

export const accountIds = createCachedSelector(
  (state, accounts) => accounts || getAccountsById(state),
  (accounts) => accounts.filter((account) => account.id).map((account) => account.id),
)(
  (state, accounts) => accounts && accounts.hashCode(),
  {cacheObject: new LruObjectCache({cacheSize: 8})},
);

export const getShowGoalAccounts = createSelector(
  (state) => getFeatureFlags(state).get('showGoalAccounts'),
  (showGoalAccounts) => showGoalAccounts,
);

export const getScheduledTransactionAccounts = createSelector(
  getAccountsById,
  getShowGoalAccounts,
  (accountsById, showGoalAccounts) => accountsById.filter((account) => (
    (showGoalAccounts || !isGoalAccount(account)) && !['INVESTMENT', 'LOAN'].includes(account.type)
  )),
);

export const getAccountsByIds = createCachedSelector(
  getAccountsById,
  (state, ids) => ids,
  (accountsById, ids) => accountsById && accountsById.filter((account) => ids && ids.includes(account.id)),
)(
  (state, ids) => ids && ids.hashCode(),
  { cacheObject: new LruMapCache({ cacheSize: 10 }) },
);

export const getFinancialInstitutionsPendo = createSelector(
  getAccountsById,
  getInstitutionLoginsById,
  (accounts, logins) => accounts.reduce((results, account) => {
    const login = logins.get(account.institutionLoginId);
    account.aggregators?.forEach?.((aggregator) => {
      const financialInstitutionPendo = `${aggregator.channel || ''}:${login?.name || ''}:${aggregator.aggStatus || ''}:${aggregator.aggStatusCode || ''}`;
      if (!results.includes(financialInstitutionPendo)) {
        results.push(financialInstitutionPendo);
      }
    });
    return results;
  }, []),
);
